/**
 * Prism grammar for LML (Lilia Markup Language).
 *
 * Prism ships grammars for a hundred languages and LML is not one of them, so
 * an `lml` fence in an Ask Lilia reply rendered as plain text while the LaTeX
 * beside it was coloured. CodeMirror already had an LML mode
 * (lib/codemirror/lml-language.ts); this is the missing half.
 *
 * Token names are chosen to land on the same theme keys the LaTeX grammar
 * uses, so one palette dresses both languages and both engines. The mapping
 * follows the same ranking: what a construct IS gets the strongest colour,
 * punctuation gets none.
 *
 *   keyword      @blocktype — the identity of the block, and the thing you
 *                scan an LML document for.
 *   attr-name    the key in [key=value].
 *   string       quoted attribute values, and embedded math.
 *   function     embedded LaTeX commands — LML content is frequently LaTeX,
 *                which is the one genuinely awkward part of the language.
 *   comment      // to end of line.
 *   punctuation  brackets, braces, equals. Uncoloured, as in LaTeX.
 *
 * Embedded LaTeX is handled by pattern rather than by nesting the whole LaTeX
 * grammar: `$…$` spans and `\command` tokens, which is what LML content
 * actually carries. Nesting the full grammar would colour prose that merely
 * contains a stray backslash, and would double the palette to say the same
 * thing twice.
 */

type PrismLike = { languages: Record<string, unknown> };

/**
 * refractor (which react-syntax-highlighter registers through) identifies a
 * grammar by `displayName` on the function itself, and rejects one without it.
 */
interface RefractorGrammar {
  (Prism: PrismLike): void;
  displayName: string;
  aliases: string[];
}

function lml(Prism: PrismLike): void {
  const embeddedLatex = {
    // $…$ and $$…$$ inside LML content.
    equation: {
      pattern: /\$\$[\s\S]*?\$\$|\$(?:\\[\s\S]|[^$\\])+\$/,
      alias: "string",
      inside: {
        "equation-command": { pattern: /\\(?:[^a-zA-Z()[\]]|[a-zA-Z*]+)/, alias: "regex" },
      },
    },
    // A bare LaTeX control sequence in prose.
    function: /\\(?:[^a-zA-Z()[\]]|[a-zA-Z*]+)/,
  };

  Prism.languages.lml = {
    comment: /\/\/.*/,

    // @heading, @equation, @theorem …
    keyword: /@[a-zA-Z_][\w-]*/,

    // [level=1, title="No-Cloning Theorem"]
    "attr-group": {
      pattern: /\[[^\]\r\n]*\]/,
      inside: {
        "attr-name": /[a-zA-Z_][\w-]*(?=\s*=)/,
        string: /"(?:\\[\s\S]|[^"\\])*"/,
        punctuation: /[[\],=]/,
      },
    },

    ...embeddedLatex,

    punctuation: /[{}]/,
  };
}

lml.displayName = "lml";
lml.aliases = [] as string[];

export default lml as RefractorGrammar;
